'use client';

import type { ReactNode } from 'react';
import type {
  ContentBlock,
  ContextSummaryContentBlock,
  WithStreamingMetadata,
} from '@tinct/core-api-models';
import type { AiChatPlugin, AiChatPluginContext } from '../types';
import {
  ContextSummaryContent,
  type ContextSummaryAction,
} from './context-summary-content';

export interface ContextSummaryPluginAction {
  label: string;
  variant?: 'default' | 'outline';
  icon?: ReactNode;
  onClick: (data: ContextSummaryContentBlock) => void;
}

export interface CreateContextSummaryPluginOptions {
  /** Plugin id — override when a chat registers more than one context_summary plugin. */
  id?: string;
  /** Human-readable labels for each data key (unused when the block's data is a plain text). */
  labels?: Record<string, string>;
  /** Action buttons rendered at the bottom of the card */
  actions?: ContextSummaryPluginAction[];
  /** Shown when the block carries no data (null or empty). */
  emptyText?: string;
  /** Whether the card is expanded when first rendered. Defaults to `true`. */
  defaultOpen?: boolean;
}

/**
 * Factory to create a context_summary block plugin with custom labels and actions.
 *
 * @example
 * ```ts
 * const campaignContextPlugin = createContextSummaryPlugin({
 *   labels: { goal: 'Goal', tone: 'Tone', keywords: 'Keywords' },
 *   actions: [
 *     { label: 'Start over', variant: 'outline', onClick: (data) => { ... } },
 *     { label: 'Confirm', onClick: (data) => { ... } },
 *   ],
 * });
 * ```
 */
export function createContextSummaryPlugin(
  options: CreateContextSummaryPluginOptions
): AiChatPlugin<'context_summary'> {
  return {
    id: options.id ?? 'context-summary',
    blockTypes: ['context_summary'],
    priority: 15,

    render(
      state: WithStreamingMetadata<ContentBlock>,
      _ctx: AiChatPluginContext
    ) {
      if (state.type !== 'context_summary') {
        return null;
      }

      const block = state as WithStreamingMetadata<ContextSummaryContentBlock>;

      const resolvedActions: ContextSummaryAction[] | undefined =
        options.actions?.map((action) => ({
          label: action.label,
          variant: action.variant,
          icon: action.icon,
          onClick: () => action.onClick(block),
        }));

      return (
        <ContextSummaryContent
          key={`context-summary-${state.index}`}
          title={block.title}
          data={block.data}
          labels={options.labels ?? {}}
          actions={resolvedActions}
          notice={block.notice}
          emptyText={options.emptyText}
          defaultOpen={options.defaultOpen}
        />
      );
    },
  };
}
